import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { alpha, type Theme } from '@mui/material/styles';
import { colors } from '../../../styles/theme/colors';
import PhoneNumberInput from '../PhoneNumberInput';
import {
  DialogZoomTransition,
  DIALOG_ZOOM_MS,
  dialogZoomSlotProps,
  dialogZoomDialogSx,
  dialogZoomPaperSx,
} from '../DialogZoomTransition';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';

export interface FormField {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'color' | 'textarea';
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  defaultValue?: string;
}

export interface FormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  fields: FormField[];
  formData: Record<string, string>;
  onFieldChange: (field: string, value: string) => void;
  onSave: () => Promise<void>;
  loading?: boolean;
  error?: string | null;
  isFormValid?: boolean;
  saveButtonText?: string;
  cancelButtonText?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'gradient' | 'simple';
}

const outlinedInputSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '14px',
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : 'transparent',
    '& fieldset': {
      borderColor: alpha(colors.success.main, 0.45),
    },
    '&:hover fieldset': {
      borderColor: colors.success.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0BB296',
      borderWidth: '2px',
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.text.disabled,
    opacity: 1,
  },
});

const FormModal: React.FC<FormModalProps> = ({
  open,
  onClose,
  title,
  icon,
  fields,
  formData,
  onFieldChange,
  onSave,
  loading = false,
  error = null,
  isFormValid = true,
  saveButtonText = 'Guardar',
  cancelButtonText = 'Cancelar',
  maxWidth = 'sm',
  variant = 'gradient',
}) => {
  const scopedOpen = useTabScopedOpen(open);

  const handleSave = async () => {
    try {
      await onSave();
    } catch (err) {
      // Error is handled by parent component
    }
  };

  const isSimpleVariant = variant === 'simple';

  return (
    <Dialog
      open={scopedOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      TransitionComponent={isSimpleVariant ? undefined : DialogZoomTransition}
      transitionDuration={isSimpleVariant ? undefined : DIALOG_ZOOM_MS}
      slotProps={isSimpleVariant ? undefined : dialogZoomSlotProps}
      sx={isSimpleVariant ? undefined : dialogZoomDialogSx}
      PaperProps={{
        sx: (theme) => {
          const dark = theme.palette.mode === 'dark';
          const paperBase = isSimpleVariant
            ? {
                borderRadius: '12px',
                overflow: 'hidden',
                margin: 'auto',
                maxWidth: '512px',
                boxShadow: dark
                  ? '0 24px 48px rgba(0,0,0,0.55)'
                  : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              }
            : dialogZoomPaperSx({
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: dark
                  ? '0 24px 48px rgba(0,0,0,0.55)'
                  : '0 20px 60px rgba(2, 6, 23, 0.18)',
                margin: 'auto',
              });
          return {
            ...paperBase,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            ...(dark && !isSimpleVariant ? { border: `1px solid ${theme.palette.divider}` } : {}),
            ...(dark && isSimpleVariant ? { border: `1px solid ${theme.palette.divider}` } : {}),
          };
        },
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          padding: isSimpleVariant ? '24px 24px 0 24px' : '16px 24px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          ...(isSimpleVariant
            ? {
                background: theme.palette.background.paper,
                backgroundImage: 'none',
                color: theme.palette.text.primary,
              }
            : {
                background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
                color: '#ffffff',
              }),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: isSimpleVariant ? '18px' : '1.25rem',
          paddingBottom: isSimpleVariant ? '24px' : undefined,
        })}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'center' }}
        >
          {!isSimpleVariant && icon}
          <Typography
            variant="h6"
            sx={(theme) => ({
              fontWeight: 600,
              color: isSimpleVariant ? theme.palette.text.primary : '#ffffff',
              fontSize: isSimpleVariant ? '18px' : '1.25rem',
            })}
          >
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={(theme) => ({
            color: isSimpleVariant ? theme.palette.text.secondary : '#ffffff',
            position: isSimpleVariant ? 'absolute' : 'relative',
            right: isSimpleVariant ? 0 : undefined,
            top: isSimpleVariant ? 0 : undefined,
            '&:hover': {
              color: isSimpleVariant
                ? theme.palette.text.primary
                : alpha(theme.palette.common.white, 0.85),
            },
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={(theme) => ({
          padding: '24px',
          backgroundColor: theme.palette.background.paper,
        })}
      >
        {error && (
          <Alert
            severity="error"
            sx={(theme) => ({
              mb: 2,
              backgroundColor: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.light,
              borderRadius: '8px',
              border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
              fontSize: '14px',
              '& .MuiAlert-icon': {
                color: theme.palette.error.main,
              },
            })}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {fields.map((field) => (
            <Box key={field.name}>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  mb: 1,
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  fontSize: '14px',
                })}
              >
                {field.label} {field.required && '*'}
              </Typography>
              {field.type === 'textarea' || field.multiline ? (
                <TextField
                  fullWidth
                  multiline
                  rows={field.rows || 3}
                  value={formData[field.name] || ''}
                  onChange={(e) => onFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  disabled={loading}
                  variant="outlined"
                  sx={(theme) => outlinedInputSx(theme)}
                />
              ) : field.type === 'color' ? (
                <TextField
                  fullWidth
                  type="color"
                  value={formData[field.name] || field.defaultValue || '#9CA3AF'}
                  onChange={(e) => onFieldChange(field.name, e.target.value)}
                  disabled={loading}
                  variant="outlined"
                  sx={(theme) => ({
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      padding: '4px',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  })}
                />
              ) : field.type === 'tel' ? (
                <PhoneNumberInput
                  value={formData[field.name] || ''}
                  onChange={(value) => onFieldChange(field.name, value)}
                  disabled={loading}
                  nationalNumberLabel="Número"
                  nationalNumberPlaceholder={field.placeholder || 'Ingrese número'}
                />
              ) : (
                <TextField
                  fullWidth
                  type={field.type || 'text'}
                  value={formData[field.name] || ''}
                  onChange={(e) => onFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  disabled={loading}
                  variant="outlined"
                  sx={(theme) => outlinedInputSx(theme)}
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          padding: isSimpleVariant ? '0 24px 24px 24px' : '16px 24px',
          borderTop: isSimpleVariant ? 'none' : `1px solid ${theme.palette.divider}`,
          backgroundColor: isSimpleVariant
            ? theme.palette.background.paper
            : theme.palette.mode === 'dark'
              ? theme.palette.background.default
              : colors.grey[50],
          display: 'flex',
          justifyContent: 'flex-end',
          gap: isSimpleVariant ? '12px' : 1.5,
          marginTop: isSimpleVariant ? '24px' : 0,
        })}
      >
        {/* Color/fondo/hover vienen de variant="tonal" del tema (ambas variantes); aquí solo tamaño/spacing. */}
        <Button
          variant="tonal"
          onClick={onClose}
          disabled={loading}
          sx={{
            minWidth: isSimpleVariant ? undefined : '120px',
            padding: isSimpleVariant ? '10px 16px' : '9px 18px',
            borderRadius: isSimpleVariant ? '8px' : '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          {cancelButtonText}
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || !isFormValid}
          variant="contained"
          sx={{
            minWidth: isSimpleVariant ? undefined : '120px',
            padding: isSimpleVariant ? '10px 16px' : '9px 18px',
            borderRadius: isSimpleVariant ? '8px' : '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
            color: '#ffffff',
            boxShadow: isSimpleVariant ? '0 2px 4px rgba(6, 182, 212, 0.2)' : 'none',
            '&:hover': {
              background: `linear-gradient(to right, #0891b2, #059669)`,
              boxShadow: isSimpleVariant ? '0 4px 12px rgba(6, 182, 212, 0.3)' : 'none',
            },
            '&:disabled': {
              background: '#9ca3af',
              color: '#ffffff',
              opacity: 0.6,
              cursor: 'not-allowed',
              boxShadow: 'none',
            },
          }}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Guardando...' : saveButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormModal;
